'use client'

import { useState, useEffect, useCallback } from 'react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useBusinessUnitStore } from '@/stores/businessUnitStore'
import { apiGet, apiPut, apiPost, apiDelete } from '@/lib/api'
import Badge from '@/components/ui/Badge'
import toast from 'react-hot-toast'
import type { User, BusinessUnit } from '@/types'
import { ExternalLink, Trash2, Plus } from 'lucide-react'

type Tab = 'business-units' | 'users' | 'sync'
type BuSubTab = 'engineers' | 'projects' | 'roles'

interface SyncLogEntry {
  started_at: string
  completed_at: string | null
  status: string
  records_fetched: number | null
  duration_seconds: number | null
  error: string | null
}

export default function SettingsPage() {
  const { isLoading, allEngineers, odooUrl, currentUser, businessUnits, mutate, dashboard } = useDashboardData()
  const setSelectedBuId = useBusinessUnitStore(s => s.setSelectedBuId)
  const [tab, setTab] = useState<Tab>('business-units')
  const [editingBuId, setEditingBuId] = useState<number | null>(null)
  const [buSubTab, setBuSubTab] = useState<BuSubTab>('engineers')

  const isEditor = currentUser?.role === 'editor' || currentUser?.role === 'admin'

  // Per-BU edit data — loaded when a BU is selected
  const [engChecked, setEngChecked] = useState<Record<string, boolean>>({})
  const [engSearch, setEngSearch] = useState('')
  const [projChecked, setProjChecked] = useState<Record<number, boolean>>({})
  const [projSearch, setProjSearch] = useState('')
  const [allProjects, setAllProjects] = useState<{ id: number; name: string; cat: string }[]>([])
  const [odooSearchResults, setOdooSearchResults] = useState<{ id: number; name: string; client: string }[]>([])
  const [odooSearching, setOdooSearching] = useState(false)
  const [projNameById, setProjNameById] = useState<Record<number, string>>({})
  const [roles, setRoles] = useState<Record<string, string>>({})
  const [activeEngForBu, setActiveEngForBu] = useState<string[]>([])

  // Users state
  const [users, setUsers] = useState<User[]>([])
  const [newUser, setNewUser] = useState<{ name: string; email: string; password: string; role: string; bu_ids: number[] }>({ name: '', email: '', password: '', role: 'viewer', bu_ids: [] })

  // BU state
  const [newBuName, setNewBuName] = useState('')

  // Sync log state
  const [syncLog, setSyncLog] = useState<SyncLogEntry[]>([])
  const [syncing, setSyncing] = useState(false)

  // Auto-select first BU when entering business-units tab
  useEffect(() => {
    if (tab === 'business-units' && editingBuId === null && businessUnits.length > 0) {
      setEditingBuId(businessUnits[0].id)
    }
  }, [tab, editingBuId, businessUnits])

  // Load BU-scoped settings when editingBuId changes
  const loadBuSettings = useCallback(async (buId: number) => {
    const [eng, proj, rolesData, dashData] = await Promise.all([
      apiGet<string[]>(`/api/settings/engineers?bu_id=${buId}`),
      apiGet<{ project_id: number; name: string }[]>(`/api/settings/projects?bu_id=${buId}`),
      apiGet<Record<string, string>>(`/api/settings/roles?bu_id=${buId}`),
      apiGet<{ data: { projects: { id: number; name: string; cat: string }[] } }>(`/api/dashboard/data?bu_id=${buId}`),
    ])
    const activeEng = eng || []
    setActiveEngForBu(activeEng)
    const ec: Record<string, boolean> = {}
    allEngineers.forEach(e => { ec[e] = activeEng.includes(e) })
    setEngChecked(ec)

    const activeProjList = proj || []
    const activeProjIds = activeProjList.map(p => p.project_id)
    const nameMap: Record<number, string> = {}
    activeProjList.forEach(p => { nameMap[p.project_id] = p.name })

    if (dashData?.data?.projects) {
      const sorted = [...dashData.data.projects].sort((a, b) => a.name.localeCompare(b.name))
      sorted.forEach(p => { if (!nameMap[p.id]) nameMap[p.id] = p.name })
      // Show all projects in cache that are either currently selected or matching the search
      setAllProjects(sorted)
      const pc: Record<number, boolean> = {}
      activeProjIds.forEach(id => { pc[id] = true })
      sorted.forEach(p => { if (!(p.id in pc)) pc[p.id] = false })
      setProjChecked(pc)
    } else {
      const pc: Record<number, boolean> = {}
      activeProjIds.forEach(id => { pc[id] = true })
      setProjChecked(pc)
    }
    setProjNameById(nameMap)

    setRoles(rolesData || {})
  }, [allEngineers])

  async function searchOdoo(q: string) {
    if (!q.trim()) {
      setOdooSearchResults([])
      return
    }
    setOdooSearching(true)
    try {
      const res = await apiGet<{ id: number; name: string; client: string }[]>(`/api/odoo/projects/search?q=${encodeURIComponent(q)}`)
      setOdooSearchResults(res || [])
    } finally {
      setOdooSearching(false)
    }
  }

  useEffect(() => {
    if (editingBuId && !isLoading) loadBuSettings(editingBuId)
  }, [editingBuId, isLoading, loadBuSettings])

  const loadUsers = useCallback(async () => {
    const res = await apiGet<User[]>('/api/users/')
    if (res) setUsers(res)
  }, [])

  useEffect(() => { if (tab === 'users' && isEditor) loadUsers() }, [tab, isEditor, loadUsers])
  useEffect(() => {
    if (tab === 'sync') {
      apiGet<{ history: SyncLogEntry[] }>('/api/sync/status').then(data => {
        if (data?.history) setSyncLog(data.history)
      })
    }
  }, [tab])

  async function saveEngineers() {
    if (!editingBuId) return
    const names = Object.entries(engChecked).filter(([, v]) => v).map(([k]) => k)
    await apiPut(`/api/settings/engineers?bu_id=${editingBuId}`, { names })
    toast.success(`${names.length} engineers active`)
    setActiveEngForBu(names)
    mutate()
  }

  async function saveProjects() {
    if (!editingBuId) return
    const selected = Object.entries(projChecked).filter(([, v]) => v).map(([k]) => parseInt(k))
    const projects = selected.map(id => {
      const name = projNameById[id] || allProjects.find(pp => pp.id === id)?.name || 'Unknown'
      return { project_id: id, name }
    })
    await apiPut(`/api/settings/projects?bu_id=${editingBuId}`, { projects })
    toast.success(`${selected.length} projects saved`)
    mutate()
  }

  async function saveRoles() {
    if (!editingBuId) return
    await apiPut(`/api/settings/roles?bu_id=${editingBuId}`, { roles })
    toast.success('Roles updated')
    mutate()
  }

  async function addUser() {
    if (!newUser.name || !newUser.email || !newUser.password) { toast.error('Fill all fields'); return }
    const res = await apiPost('/api/users/', newUser)
    if (res) {
      toast.success('User added')
      setNewUser({ name: '', email: '', password: '', role: 'viewer', bu_ids: [] })
      loadUsers()
    }
  }

  async function updateUserBus(userId: number, bu_ids: number[], role?: string) {
    await apiPut(`/api/users/${userId}`, { bu_ids, ...(role !== undefined ? { role } : {}) })
    loadUsers()
    toast.success('User updated')
  }

  async function deleteUser(id: number) {
    if (!confirm('Remove this user?')) return
    await apiDelete(`/api/users/${id}`)
    loadUsers()
  }

  async function addBu() {
    if (!newBuName.trim()) return
    const res = await apiPost<BusinessUnit>('/api/business-units/', { name: newBuName.trim() })
    if (res) {
      toast.success(`Created "${newBuName}"`)
      setNewBuName('')
      mutate()
      setEditingBuId(res.id)
    }
  }

  async function deleteBu(bu: BusinessUnit) {
    if (!confirm(`Delete business unit "${bu.name}"? This will remove all its engineers, projects, and user assignments.`)) return
    const ok = await apiDelete(`/api/business-units/${bu.id}`)
    if (ok) {
      toast.success(`Deleted "${bu.name}"`)
      const remaining = businessUnits.filter(b => b.id !== bu.id)
      if (remaining.length > 0) {
        setEditingBuId(remaining[0].id)
        if (dashboard?.current_bu_id === bu.id) setSelectedBuId(remaining[0].id)
      } else {
        setEditingBuId(null)
      }
      mutate()
    }
  }

  async function renameBu(bu: BusinessUnit, newName: string) {
    if (!newName.trim() || newName === bu.name) return
    await apiPut(`/api/business-units/${bu.id}`, { name: newName.trim() })
    toast.success('Renamed')
    mutate()
  }

  if (isLoading) return <div className="text-slate-400 text-center py-16">Loading...</div>

  const tabs: { key: Tab; label: string; editor?: boolean }[] = [
    { key: 'business-units', label: 'Business Units' },
    { key: 'users', label: 'Users', editor: true },
    { key: 'sync', label: 'Sync Log' },
  ]

  const filteredEng = allEngineers.filter(e => e.toLowerCase().includes(engSearch.toLowerCase()))
  const filteredProj = allProjects.filter(p => p.name.toLowerCase().includes(projSearch.toLowerCase()))
  const editingBu = businessUnits.find(b => b.id === editingBuId) || null

  return (
    <div className="max-w-4xl">
      <h2 className="text-xl font-semibold mb-4">Settings</h2>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-slate-200 dark:border-slate-800">
        {tabs.filter(t => !t.editor || isEditor).map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Business Units tab */}
      {tab === 'business-units' && (
        <div>
          {/* BU pills */}
          <div className="flex items-center flex-wrap gap-2 mb-5">
            {businessUnits.map(bu => (
              <button
                key={bu.id}
                onClick={() => setEditingBuId(bu.id)}
                className={`px-4 py-2 text-sm font-medium border transition-colors ${
                  editingBuId === bu.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-blue-400'
                }`}
              >
                {bu.name}
              </button>
            ))}
            {isEditor && (
              <div className="flex gap-1 ml-2">
                <input value={newBuName} onChange={e => setNewBuName(e.target.value)} placeholder="New BU name…"
                  onKeyDown={e => { if (e.key === 'Enter') addBu() }}
                  className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none focus:border-blue-500 w-44" />
                <button onClick={addBu} className="px-3 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 font-medium flex items-center gap-1">
                  <Plus size={14} /> Add
                </button>
              </div>
            )}
          </div>

          {/* Selected BU detail */}
          {editingBu && (
            <div className="border border-slate-200 dark:border-slate-800 p-5">
              {/* BU header */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100 dark:border-slate-800">
                <input
                  defaultValue={editingBu.name}
                  key={editingBu.id}
                  onBlur={e => renameBu(editingBu, e.target.value)}
                  disabled={!isEditor}
                  className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:bg-slate-50 dark:focus:bg-slate-800/50 px-1 py-0.5 max-w-sm"
                />
                {isEditor && (
                  <button onClick={() => deleteBu(editingBu)} className="text-slate-400 hover:text-red-500 text-sm flex items-center gap-1" title="Delete BU">
                    <Trash2 size={14} /> Delete BU
                  </button>
                )}
              </div>

              {/* Sub-tabs */}
              <div className="flex gap-1 mb-4 border-b border-slate-100 dark:border-slate-800">
                {(['engineers', 'projects', 'roles'] as BuSubTab[]).map(t => (
                  <button
                    key={t}
                    onClick={() => setBuSubTab(t)}
                    className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors capitalize ${
                      buSubTab === t
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Engineers sub-tab */}
              {buSubTab === 'engineers' && (
                <div>
                  <p className="text-xs text-slate-400 mb-3">Engineers tracked under this business unit.</p>
                  <div className="flex gap-2 mb-3">
                    <input value={engSearch} onChange={e => setEngSearch(e.target.value)} placeholder="Search…"
                      className="flex-1 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none focus:border-blue-500" />
                    <button onClick={() => { const nc = { ...engChecked }; filteredEng.forEach(e => nc[e] = true); setEngChecked(nc) }}
                      className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700">All</button>
                    <button onClick={() => { const nc = { ...engChecked }; filteredEng.forEach(e => nc[e] = false); setEngChecked(nc) }}
                      className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700">None</button>
                  </div>
                  <div className="max-h-[40vh] overflow-y-auto border border-slate-200 dark:border-slate-800 mb-3">
                    {filteredEng.map(eng => (
                      <label key={eng} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer text-sm border-b border-slate-100 dark:border-slate-800/50">
                        <input type="checkbox" checked={engChecked[eng] || false} onChange={e => setEngChecked({ ...engChecked, [eng]: e.target.checked })}
                          disabled={!isEditor} className="accent-blue-600" />
                        <span className="flex-1">{eng}</span>
                        <span className="text-sm text-slate-400">{engChecked[eng] ? 'active' : 'hidden'}</span>
                      </label>
                    ))}
                  </div>
                  {isEditor && (
                    <div className="flex justify-end">
                      <button onClick={saveEngineers} className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 font-medium">Save</button>
                    </div>
                  )}
                </div>
              )}

              {/* Projects sub-tab */}
              {buSubTab === 'projects' && (
                <div>
                  <p className="text-xs text-slate-400 mb-3">Projects included under this business unit. All Odoo projects are available — search and tick what belongs to this BU.</p>
                  <div className="flex gap-2 mb-3">
                    <input value={projSearch} onChange={e => setProjSearch(e.target.value)} placeholder="Search projects…"
                      className="flex-1 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none focus:border-blue-500" />
                    <button onClick={() => { const nc = { ...projChecked }; filteredProj.forEach(p => nc[p.id] = true); setProjChecked(nc) }}
                      className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700">All</button>
                    <button onClick={() => { const nc = { ...projChecked }; filteredProj.forEach(p => nc[p.id] = false); setProjChecked(nc) }}
                      className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700">None</button>
                  </div>
                  <div className="max-h-[50vh] overflow-y-auto border border-slate-200 dark:border-slate-800 mb-3">
                    {filteredProj.map(p => (
                      <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer text-sm border-b border-slate-100 dark:border-slate-800/50">
                        <input type="checkbox" checked={projChecked[p.id] || false} onChange={e => setProjChecked({ ...projChecked, [p.id]: e.target.checked })}
                          disabled={!isEditor} className="accent-blue-600" />
                        <Badge variant={p.cat === 'MSO' ? 'mso' : p.cat === 'PSO' ? 'pso' : 'internal'}>{p.cat}</Badge>
                        <span className="flex-1 truncate">{p.name}</span>
                        <a href={`${odooUrl}/odoo/project.project/${p.id}/tasks`} target="_blank" rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700" title="Open in Odoo">
                          <ExternalLink size={12} />
                        </a>
                        <span className="text-sm text-slate-400">{projChecked[p.id] ? 'active' : 'hidden'}</span>
                      </label>
                    ))}
                  </div>
                  {isEditor && (
                    <div className="flex justify-end">
                      <button onClick={saveProjects} className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 font-medium">Save</button>
                    </div>
                  )}
                </div>
              )}

              {/* Roles sub-tab */}
              {buSubTab === 'roles' && (
                <div>
                  <p className="text-xs text-slate-400 mb-3"><strong>L3</strong> = Tech Lead, <strong>L2</strong> = Senior, <strong>L1</strong> = Engineer.</p>
                  {activeEngForBu.length === 0 ? (
                    <p className="text-sm text-slate-400 py-4 text-center">No active engineers in this BU yet. Add some in the Engineers tab.</p>
                  ) : (
                    <>
                      <div className="border border-slate-200 dark:border-slate-800 mb-3">
                        {activeEngForBu.map(eng => (
                          <div key={eng} className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800/50 text-sm">
                            <span>{eng}</span>
                            <select
                              value={roles[eng] || 'L1'}
                              onChange={e => setRoles({ ...roles, [eng]: e.target.value })}
                              disabled={!isEditor}
                              className="text-sm border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1 focus:outline-none focus:border-blue-500"
                            >
                              <option value="L1">L1</option>
                              <option value="L2">L2</option>
                              <option value="L3">L3</option>
                            </select>
                          </div>
                        ))}
                      </div>
                      {isEditor && (
                        <div className="flex justify-end">
                          <button onClick={saveRoles} className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 font-medium">Save</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Users tab (editor only) */}
      {tab === 'users' && isEditor && (
        <div>
          <p className="text-sm text-slate-400 mb-3">Manage dashboard login accounts and business unit access.</p>

          <div className="border border-slate-200 dark:border-slate-800 mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  {['Name', 'Email', 'Role', 'Business Units', ''].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-sm uppercase tracking-wider text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800/50">
                    <td className="px-3 py-2">{u.name}</td>
                    <td className="px-3 py-2 text-slate-400">{u.email}</td>
                    <td className="px-3 py-2">
                      <select
                        value={u.role}
                        onChange={e => updateUserBus(u.id, u.bu_ids || [], e.target.value)}
                        className="text-sm border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1 focus:outline-none focus:border-blue-500"
                        disabled={u.id === currentUser?.id}
                      >
                        <option value="viewer">viewer</option>
                        <option value="editor">editor</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {u.role === 'editor' || u.role === 'admin' ? (
                        <span className="text-xs text-slate-400">all (editor)</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {businessUnits.map(bu => {
                            const checked = (u.bu_ids || []).includes(bu.id)
                            return (
                              <label key={bu.id} className="flex items-center gap-1 text-xs cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={e => {
                                    const next = e.target.checked
                                      ? [...(u.bu_ids || []), bu.id]
                                      : (u.bu_ids || []).filter(id => id !== bu.id)
                                    updateUserBus(u.id, next)
                                  }}
                                  className="accent-blue-600"
                                />
                                <span>{bu.name}</span>
                              </label>
                            )
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {u.id !== currentUser?.id && (
                        <button onClick={() => deleteUser(u.id)} className="text-slate-300 hover:text-red-500" title="Remove">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-slate-200 dark:border-slate-800 p-4">
            <div className="text-sm font-semibold mb-3">Add User</div>
            <div className="grid grid-cols-2 gap-3">
              <input value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} placeholder="Name"
                className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none focus:border-blue-500" />
              <input value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="Email" type="email"
                className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none focus:border-blue-500" />
              <input value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Password" type="password"
                className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none focus:border-blue-500" />
              <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none focus:border-blue-500">
                <option value="viewer">Viewer (sees assigned BUs only)</option>
                <option value="editor">Editor (full access)</option>
              </select>
              {newUser.role === 'viewer' && (
                <div className="col-span-2">
                  <div className="text-xs text-slate-400 mb-1">Assign to business units:</div>
                  <div className="flex flex-wrap gap-3">
                    {businessUnits.map(bu => (
                      <label key={bu.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newUser.bu_ids.includes(bu.id)}
                          onChange={e => setNewUser({
                            ...newUser,
                            bu_ids: e.target.checked
                              ? [...newUser.bu_ids, bu.id]
                              : newUser.bu_ids.filter(id => id !== bu.id),
                          })}
                          className="accent-blue-600"
                        />
                        <span>{bu.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="col-span-2 flex justify-end">
                <button onClick={addUser} className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 font-medium">Add User</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync Log tab */}
      {tab === 'sync' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-400">Daily sync at 07:00 CET. Retries every 10 minutes on failure.</p>
            {isEditor && (
              <button
                onClick={async () => {
                  setSyncing(true)
                  try {
                    await apiPost('/api/sync/trigger', {})
                    toast.success('Sync started')
                    const poll = setInterval(async () => {
                      const data = await apiGet<{ history: SyncLogEntry[] }>('/api/sync/status')
                      if (data?.history) {
                        setSyncLog(data.history)
                        const latest = data.history[0]
                        if (latest && latest.status !== 'running') {
                          clearInterval(poll)
                          setSyncing(false)
                          if (latest.status === 'success') {
                            toast.success(`Sync complete — ${latest.records_fetched} records in ${latest.duration_seconds}s`)
                            mutate()
                          } else {
                            toast.error('Sync failed')
                          }
                        }
                      }
                    }, 5000)
                  } catch {
                    toast.error('Failed to trigger sync')
                    setSyncing(false)
                  }
                }}
                disabled={syncing}
                className={`px-4 py-2 text-sm font-medium ${syncing ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
              >
                {syncing ? 'Syncing...' : 'Force Sync'}
              </button>
            )}
          </div>
          <div className="border border-slate-200 dark:border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  {['Time', 'Completed', 'Status', 'Records', 'Duration', 'Error'].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {syncLog.map((entry, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-slate-800/50">
                    <td className="px-3 py-2 text-[0.65rem]">{new Date(entry.started_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-[0.65rem] text-slate-400">{entry.completed_at ? new Date(entry.completed_at).toLocaleTimeString() : '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant={entry.status === 'success' ? 'success' : entry.status === 'running' ? 'warning' : 'danger'}>{entry.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-slate-400">{entry.records_fetched ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-400">{entry.duration_seconds ? `${entry.duration_seconds}s` : '—'}</td>
                    <td className="px-3 py-2 text-red-500 text-sm truncate max-w-[200px]" title={entry.error || ''}>{entry.error || '—'}</td>
                  </tr>
                ))}
                {syncLog.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400">No sync history available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
